import { useEffect, useState } from "react";
import {
  Loader2,
  Pencil,
  Search,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Input } from "@sportscanner/ui";
import { apiGet } from "@/lib/api";
import { SPORT_KEYS } from "@/lib/sports";
import ClubSettingsDialog, {
  type ClubSettingsRow,
} from "./club-settings-dialog";
import DeleteClubDialog from "./delete-club-dialog";

interface ClubListItem extends ClubSettingsRow {
  address: string;
  setupComplete: boolean;
  _count: { sports: number; courts: number };
}

interface ClubsResponse {
  clubs: ClubListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const PAGE_SIZE = 20;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-2.5 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
      {children}
    </th>
  );
}

export default function ClubsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ClubListItem | null>(null);
  const [deleting, setDeleting] = useState<ClubListItem | null>(null);

  const [search, setSearch] = useState("");
  const [sports, setSports] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebouncedValue(search, 300);
  const sportsKey = sports.join(",");

  // Any filter change resets to the first page so results stay coherent.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sportsKey]);

  function toggleSport(key: string) {
    setSports((prev) =>
      prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key],
    );
  }

  const queryString = (() => {
    const params = new URLSearchParams();
    if (debouncedSearch.trim()) params.set("q", debouncedSearch.trim());
    if (sportsKey) params.set("sport", sportsKey);
    params.set("page", String(page));
    params.set("pageSize", String(PAGE_SIZE));
    return params.toString();
  })();

  const clubsQuery = useQuery({
    queryKey: ["admin", "clubs", "list", queryString],
    queryFn: () => apiGet<ClubsResponse>(`/api/admin/clubs?${queryString}`),
    placeholderData: keepPreviousData,
  });

  async function invalidate() {
    await queryClient.invalidateQueries({ queryKey: ["admin", "clubs"] });
  }

  const data = clubsQuery.data;
  const clubs = data?.clubs ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const hasFilters = search.trim().length > 0 || sports.length > 0;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="font-heading text-xl sm:text-2xl font-bold text-neutral-800 mb-1">
          {t("clubs.title")}
        </h1>
        <p className="text-sm text-neutral-500">{t("clubs.subtitle")}</p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:flex-wrap">
        <div className="relative w-full sm:w-64">
          <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("clubs.filters.searchPlaceholder")}
            className="pl-9 bg-white border-neutral-200 w-full"
          />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {SPORT_KEYS.map((key) => {
            const active = sports.includes(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggleSport(key)}
                aria-pressed={active}
                className={`inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors cursor-pointer ${
                  active
                    ? "bg-primary-600 text-white border-primary-600"
                    : "bg-white text-neutral-600 border-neutral-200 hover:bg-neutral-50"
                }`}
              >
                {t(`sports.${key}`)}
              </button>
            );
          })}
        </div>
        {hasFilters && (
          <button
            onClick={() => {
              setSearch("");
              setSports([]);
            }}
            className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700 cursor-pointer px-2 py-1"
          >
            <X className="w-4 h-4" />
            {t("clubs.filters.clear")}
          </button>
        )}
      </div>

      {clubsQuery.isLoading ? (
        <div className="bg-white rounded-xl border border-neutral-200 p-8 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
        </div>
      ) : clubsQuery.isError ? (
        <div className="bg-white rounded-xl border border-neutral-200 p-8 text-center">
          <p className="text-sm text-neutral-500">
            {t("errors.api.INTERNAL_ERROR")}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div
            className={`bg-white rounded-xl border border-neutral-200 overflow-hidden ${
              clubsQuery.isFetching ? "opacity-80 transition-opacity" : ""
            }`}
          >
            {clubs.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <p className="text-sm text-neutral-500">
                  {hasFilters ? t("clubs.noResults") : t("clubs.empty")}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-neutral-50 border-b border-neutral-200">
                    <tr>
                      <Th>{t("clubs.table.name")}</Th>
                      <Th>{t("clubs.table.url")}</Th>
                      <Th>{t("clubs.table.address")}</Th>
                      <Th>{t("clubs.table.sports")}</Th>
                      <Th>{t("clubs.table.courts")}</Th>
                      <Th>{t("clubs.table.status")}</Th>
                      <th className="px-4 py-2.5 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        {t("clubs.table.actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {clubs.map((club) => (
                      <tr
                        key={club.id}
                        className="hover:bg-neutral-50 transition-colors"
                      >
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-neutral-800">
                          {club.name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-500 font-mono">
                          /{club.slug}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-700 max-w-[16rem] truncate">
                          {club.address || "—"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-700">
                          {club._count.sports}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-700">
                          {club.maxCourts !== null
                            ? `${club._count.courts} / ${club.maxCourts}`
                            : club._count.courts}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                              club.setupComplete
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-amber-100 text-amber-700"
                            }`}
                          >
                            {club.setupComplete
                              ? t("clubs.status.active")
                              : t("clubs.status.setupPending")}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <div className="inline-flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => setEditing(club)}
                              className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 font-medium text-sm cursor-pointer"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                              {t("clubs.table.manage")}
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleting(club)}
                              className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 font-medium text-sm cursor-pointer"
                              aria-label={t("clubs.table.delete")}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              {t("clubs.table.delete")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-neutral-600">
              <span>{t("clubs.pagination.totalCount", { count: total })}</span>
              <div className="flex items-center gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t("clubs.pagination.previous")}
                </button>
                <span className="px-2 text-sm text-neutral-500">
                  {t("clubs.pagination.page", { page, total: totalPages })}
                </span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {t("clubs.pagination.next")}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <ClubSettingsDialog
        club={editing}
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        onSaved={invalidate}
      />
      <DeleteClubDialog
        club={deleting}
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        onDeleted={invalidate}
      />
    </div>
  );
}
